import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import {
  modalContentByWorkshop,
  type MapContent,
} from "@/features/workshop/modals/modal-content";
import { resolveField } from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { Field, SectionFrame, TextInput } from "../editor-kit";
import { sameJson } from "../editor-utils";

/** Logistics (venue / map) editor — six flat strings. */
export function LogisticsEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const def = modalContentByWorkshop[wsId].map;
  const current = resolveField(override, "map", def);
  const [draft, setDraft] = useState<MapContent>(current);
  const set = (patch: Partial<MapContent>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <SectionFrame
      description="Venue, address, and logistics shown in the Logistics modal."
      overridden={override?.map !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() => setEffectiveOverride(wsId, { map: draft })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "map");
        setDraft(def);
      }}
    >
      <Field label="Venue name">
        <TextInput value={draft.venueName} onChange={(e) => set({ venueName: e.target.value })} />
      </Field>
      <Field label="Address">
        <TextInput value={draft.address} onChange={(e) => set({ address: e.target.value })} />
      </Field>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Date">
          <TextInput value={draft.date} onChange={(e) => set({ date: e.target.value })} />
        </Field>
        <Field label="Time">
          <TextInput value={draft.time} onChange={(e) => set({ time: e.target.value })} />
        </Field>
      </div>
      <Field label="Parking">
        <TextInput value={draft.parking} onChange={(e) => set({ parking: e.target.value })} />
      </Field>
      <Field label="Refreshments">
        <TextInput
          value={draft.refreshments}
          onChange={(e) => set({ refreshments: e.target.value })}
        />
      </Field>
    </SectionFrame>
  );
}
